import { useState, useEffect } from 'react';
import { Package, Calendar, User, Truck, ExternalLink, Send } from 'lucide-react';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { ShippingModal } from './ShippingModal';

export function CriticalBookingsAlert({ onRefresh }) {
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [criticalDaysThreshold, setCriticalDaysThreshold] = useState(3);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showShippingModal, setShowShippingModal] = useState(false);

  const fetchUpcomingBookings = async () => {
    try {
      const [bookingsRes, configRes] = await Promise.all([
        api.getUpcomingBookings(),
        api.get('/admin/config'),
      ]);

      setUpcomingBookings(bookingsRes.bookings || []);

      if (configRes.success && configRes.config) {
        setCriticalDaysThreshold(configRes.config.criticalDaysThreshold);
      }
    } catch (error) {
      console.error('Error fetching upcoming bookings:', error);
      if (loading) {
        toast.error('Fehler beim Laden der Buchungen');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcomingBookings();
    const interval = setInterval(fetchUpcomingBookings, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (onRefresh) {
      fetchUpcomingBookings();
    }
  }, [onRefresh]);

  const getDaysUntilEvent = (booking) => {
    const eventDate = new Date(booking.startDate);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleOpenShippingModal = (booking) => {
    setSelectedBooking(booking);
    setShowShippingModal(true);
  };

  const handleCloseShippingModal = () => {
    setShowShippingModal(false);
    setSelectedBooking(null);
  };

  const handleShippingSuccess = () => {
    fetchUpcomingBookings();
    if (onRefresh) {
      onRefresh();
    }
  };

  if (loading) {
    return null;
  }

  // Filter critical bookings (≤ criticalDaysThreshold days until event)
  const criticalBookings = upcomingBookings.filter(
    booking => getDaysUntilEvent(booking) <= criticalDaysThreshold
  );

  if (criticalBookings.length === 0) {
    return null;
  }

  return (
    <Card className="mb-8">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">⚠️ Kritische Fotoboxen</h2>
            <p className="text-sm text-gray-500 mt-1">
              Events in den nächsten {criticalDaysThreshold} Tagen - sofort versenden!
            </p>
          </div>
          <Badge variant="error" size="lg">
            {criticalBookings.length} {criticalBookings.length === 1 ? 'Buchung' : 'Buchungen'}
          </Badge>
        </div>

        <div className="space-y-3">
          {criticalBookings.map((booking) => {
            const daysUntil = getDaysUntilEvent(booking);
            const isCritical = true; // All bookings here are critical

            return (
              <div
                key={booking.bookingId}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  isCritical
                    ? 'bg-red-50 border-red-300'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-600" />
                        <p className="font-medium text-gray-900">
                          {booking.customerName || booking.customerEmail}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        <span>{booking.productTitle}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Event: {formatDate(booking.startDate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        <span>
                          {booking.shippingStatus === 'not_shipped' ? 'Noch nicht versendet' : 'In Vorbereitung'}
                        </span>
                      </div>
                    </div>

                    {booking.trackingNumber && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Tracking:</span>
                        <span className="font-mono text-gray-700">{booking.trackingNumber}</span>
                        <a
                          href={`https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode=${booking.trackingNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          DHL
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <div className={`text-right ${isCritical ? 'px-3 py-2 bg-red-600 text-white rounded-lg' : ''}`}>
                      <div className={`text-2xl font-bold ${isCritical ? '' : 'text-gray-900'}`}>
                        {daysUntil}
                      </div>
                      <div className={`text-xs uppercase ${isCritical ? 'text-red-100' : 'text-gray-500'}`}>
                        {daysUntil === 1 ? 'Tag' : 'Tage'}
                      </div>
                    </div>

                    {booking.shippingStatus === 'not_shipped' && (
                      <Button
                        onClick={() => handleOpenShippingModal(booking)}
                        variant="primary"
                        size="sm"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Jetzt versenden
                      </Button>
                    )}

                    {booking.shippingStatus === 'preparing' && (
                      <Button
                        onClick={() => handleOpenShippingModal(booking)}
                        variant="success"
                        size="sm"
                      >
                        <Truck className="w-4 h-4 mr-2" />
                        Versand abschließen
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Shipping Modal */}
      {showShippingModal && selectedBooking && (
        <ShippingModal
          booking={selectedBooking}
          onClose={handleCloseShippingModal}
          onSuccess={handleShippingSuccess}
        />
      )}
    </Card>
  );
}

export default CriticalBookingsAlert;
